var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useContext, useEffect, useState } from "react";
import { UXPContext } from "@core/context/Context";
import { Dropdown } from "@widget/popups/Dropdown";
import { hasValue } from "@utils";
import DynamicForm from "@widget/forms/DynamicForm/DynamicForm";
import Badge from "@widget/other/Badge";
import Icon from "@icon/Icon";
import Select from "@widget/forms/inputs/Select";
import DropdownIndicator from "@widget/other/DropdownIndicator";
import { cloneDeep } from 'lodash-es';
import classNames from "classnames";
import { AdvancedFilterPanel } from "./AdvancedFilterPanel";
/**
 * @export
 * Checks if the given filters object is a SimpleFilter type
 * param filters - The filters object to check
 * returns True if filters is a SimpleFilter
 */
export function isSimpleFilter(filters) {
    return filters === null || filters === void 0 ? void 0 : filters.hasOwnProperty('filters');
}
function cleanValue(value) {
    // Handle null/undefined
    if (value == null) {
        return undefined;
    }
    // Handle primitives
    if (['string', 'number'].includes(typeof value)) {
        return hasValue(value, true, true) ? value : undefined;
    }
    if (typeof value === 'boolean') {
        return value || undefined;
    }
    // Handle Date objects (before array/object check)
    if (value instanceof Date) {
        return isNaN(value.getTime()) ? undefined : value;
    }
    // Handle arrays
    if (Array.isArray(value)) {
        const cleaned = value.filter(Boolean);
        return cleaned.length > 0 ? cleaned : undefined;
    }
    // Handle plain objects
    if (typeof value === 'object') {
        const cleaned = Object.entries(value).reduce((obj, [key, val]) => {
            const cleanedVal = cleanValue(val);
            if (cleanedVal !== undefined) {
                obj[key] = cleanedVal;
            }
            return obj;
        }, {});
        return Object.keys(cleaned).length > 0 ? cleaned : undefined;
    }
    return value;
}
/**
 * @export
 * Removes empty or invalid filter values from the filters object
 * param filters - The filters object to clean
 * returns Cleaned filters object with only valid values
 */
export function cleanFilters(filters) {
    if (!isSimpleFilter(filters)) {
        return filters;
    }
    const cleanedFilters = Object.entries((filters === null || filters === void 0 ? void 0 : filters.filters) || {}).reduce((result, [key, value]) => {
        const cleaned = cleanValue(value);
        if (cleaned !== undefined) {
            result[key] = cleaned;
        }
        return result;
    }, {});
    return { filters: cleanedFilters };
}
/**
 * @export
 * Checks if the filters object contains any valid filter values
 * param filters - The filters object to check
 * returns True if filters contains valid filter values
 */
export function hasFilters(filters) {
    const cleaned = cleanFilters(filters);
    if (isSimpleFilter(cleaned))
        return !!Object.keys((cleaned === null || cleaned === void 0 ? void 0 : cleaned.filters) || {}).length;
    return false;
}
/**
 * @export
 * Gets the count of active filters
 * param filters - The filters object to count
 * returns The number of active filters
 */
export function getFilterCount(filters, keys) {
    const cleaned = cleanFilters(filters);
    if (!cleaned || !isSimpleFilter(cleaned))
        return 0;
    const allKeys = Object.keys(cleaned.filters);
    return keys ? allKeys.filter(k => keys.has(k)).length : allKeys.length;
}
const FilterViewBase = ({ isOpen, onToggle, appliedFilters, onChange, formFields, renderCustom, getFilterCount: customFilterCount, filterMode = 'simple', onFilterModeChange, advancedConfig, advancedQuestion = '', onAdvancedQuestionChange, onAdvancedSubmit, advancedLoading = false, hasActivePipeline = false, advancedError, activePipeline, onEditRefinement, pipelineRefined, hasOriginalPipeline, onRestoreOriginalPipeline, }) => {
    const context = useContext(UXPContext);
    const userFacingKeys = new Set((formFields || []).flatMap(s => (s.fields || []).map((f) => f.name)));
    const hasAdvanced = !!advancedConfig;
    const [queryEngineAvailable, setQueryEngineAvailable] = useState(null);
    const [queryEngineChecking, setQueryEngineChecking] = useState(false);
    useEffect(() => {
        var _a, _b;
        if (filterMode !== 'advanced' || !hasAdvanced) {
            setQueryEngineAvailable(null);
            return;
        }
        setQueryEngineChecking(true);
        const qe = (_b = (_a = window === null || window === void 0 ? void 0 : window.QueryBuilderComponents) === null || _a === void 0 ? void 0 : _a.useQueryEngineService) === null || _b === void 0 ? void 0 : _b.call(_a, context);
        if (!qe) {
            setQueryEngineAvailable(false);
            setQueryEngineChecking(false);
            return;
        }
        qe.checkStatus()
            .then((res) => setQueryEngineAvailable((res === null || res === void 0 ? void 0 : res.active) === true))
            .catch(() => setQueryEngineAvailable(false))
            .finally(() => setQueryEngineChecking(false));
    }, [filterMode, hasAdvanced]);
    function renderDefault() {
        if ((formFields === null || formFields === void 0 ? void 0 : formFields.length) == 0)
            return context.$L('uxp-core.text.no-filters');
        // Rebuilding the seeded structure every render is safe: DynamicForm only reseeds
        // its form data when field names/types/values actually change (content signature),
        // so un-applied user edits survive parent re-renders (e.g. table refreshes).
        const updatedDormFields = cloneDeep(formFields || []).map(fs => {
            var _a;
            fs.columns = 1;
            for (const f of fs.fields || []) {
                const v = ((_a = appliedFilters === null || appliedFilters === void 0 ? void 0 : appliedFilters.filters) === null || _a === void 0 ? void 0 : _a[f.name]) || null;
                if (!!v)
                    f.value = v;
            }
            return fs;
        });
        return _jsx(DynamicForm, { formStructure: updatedDormFields, onSubmit: (data) => __awaiter(this, void 0, void 0, function* () {
                onChange(cleanFilters({ filters: data }));
                onToggle();
            }), onCancel: () => {
                onChange({ filters: {} });
                onToggle();
            }, submitButtonLabel: context.$L('uxp-core.text.apply'), submitButtonLoadingLabel: context.$L('uxp-core.text.applying'), submitButtonIcon: "fas check", cancelButtonLabel: context.$L('uxp-core.text.clear'), buttonSize: 'small' });
    }
    const badgeCount = filterMode === 'advanced'
        ? (hasActivePipeline ? 1 : 0)
        // dev-provided counter wins; else count formFields keys; fully-custom renders
        // have no formFields — count all applied keys instead of none
        : customFilterCount
            ? customFilterCount(appliedFilters)
            : getFilterCount(appliedFilters, userFacingKeys.size > 0 ? userFacingKeys : undefined);
    return _jsx(Dropdown, { isOpen: isOpen, onToggle: onToggle, trigger: _jsx("div", { className: classNames("uxpcore_osc__filterbutton", {
                "uxpcore_osc__filterbutton--active": isOpen,
                "uxpcore_osc__filterbutton--advanced": filterMode === 'advanced' && hasActivePipeline,
            }), children: _jsx(Badge, { count: badgeCount, max: 9, children: _jsx(Icon, { icon: 'fal filter' }) }) }), content: _jsxs("div", { className: classNames("uxpcore_osc__filterpanel", { "uxpcore_osc__filterpanel--has-header": hasAdvanced }), children: [hasAdvanced && (() => {
                    const modeOptions = [
                        { label: context.$L('uxp-core.text.simple'), value: 'simple', icon: 'fal sliders-h' },
                        { label: context.$L('uxp-core.text.advanced'), value: 'advanced', icon: 'fal magic' },
                    ];
                    const selected = modeOptions.find(o => o.value === filterMode);
                    return (_jsxs("div", { className: "uxpcore_osc__filter_header", children: [_jsx("span", { className: "uxpcore_osc__filter_header__label", children: context.$L('uxp-core.text.filters') }), _jsx(Select, { options: modeOptions, selected: filterMode, onChange: (value) => onFilterModeChange === null || onFilterModeChange === void 0 ? void 0 : onFilterModeChange(value), iconField: 'icon', renderPlaceholder: {
                                    renderCustomPill: () => (_jsxs("div", { className: "uxpcore_osc__filter_mode_pill", children: [_jsx("div", { className: "uxpcore_osc__filter_mode_pill__icon", children: _jsx(Icon, { icon: selected === null || selected === void 0 ? void 0 : selected.icon }) }), _jsx("span", { children: selected === null || selected === void 0 ? void 0 : selected.label }), _jsx(DropdownIndicator, { isOpen: false })] }))
                                } })] }));
                })(), filterMode === 'advanced' && hasAdvanced
                    ? queryEngineChecking || queryEngineAvailable === null
                        ? _jsx("div", { className: "uxpcore_osc__advanced_filter__unavailable", children: context.$L('uxp-core.text.loading') })
                        : queryEngineAvailable === false
                            ? _jsx("div", { className: "uxpcore_osc__advanced_filter__unavailable", children: context.$L('uxp-core.text.query-engine-unavailable') })
                            : _jsx(AdvancedFilterPanel, { question: advancedQuestion, onQuestionChange: onAdvancedQuestionChange, onSubmit: onAdvancedSubmit, loading: advancedLoading, error: advancedError, pipeline: activePipeline, onEditRefinement: onEditRefinement, pipelineRefined: pipelineRefined, hasOriginalPipeline: hasOriginalPipeline, onRestoreOriginalPipeline: onRestoreOriginalPipeline })
                    : (!!renderCustom)
                        ? renderCustom(appliedFilters, onChange, true)
                        : renderDefault()] }) });
};
/**
 * @export
 * FilterView component provides a dropdown interface for filtering data.
 * It displays a filter button with a badge showing the active filter count
 * and renders a dropdown panel with filter controls.
 *
 * @example
 * ```
 * <FilterView
 *   isOpen={isFilterOpen}
 *   onToggle={() => setIsFilterOpen(!isFilterOpen)}
 *   appliedFilters={currentFilters}
 *   onChange={(filters) => setCurrentFilters(filters)}
 *   formFields={filterFormStructure}
 * />
 * ```
 *
 * @example
 * ```
 * <FilterView
 *   isOpen={isFilterOpen}
 *   onToggle={() => setIsFilterOpen(!isFilterOpen)}
 *   appliedFilters={currentFilters}
 *   onChange={(filters) => setCurrentFilters(filters)}
 *   formFields={filterFormStructure}
 *   renderCustom={(filters, onChange, singleColumn) => (
 *     <CustomFilterPanel
 *       filters={filters}
 *       onChange={onChange}
 *       singleColumn={singleColumn}
 *     />
 *   )}
 * />
 * ```
 */
export const FilterView = memo(FilterViewBase);
